import { formResponsesQuery } from "../http/form-responses-query";
import type {
  WebhookDeliveryAttempt,
  WebhookInEndpoint,
  WebhookOutEndpoint,
} from "../webhook-types";
import type { SystemRepliesView, UnmatchedQuestionView } from "../automation-types";
import type {
  BroadcastCostPreview,
  BroadcastOverview,
  BroadcastStats,
  BroadcastPreflight,
} from "../broadcast-types";
import type {
  CannedReply,
  CommerceTestResult,
  CustomApiConnectResult,
  ApiKeysResponse,
  ConnectedApp,
  CreatedApiKey,
  WorkspaceDefaults,
} from "../types";
import type { DealView, PipelineView } from "../crm-types";
import type { IntegrationsHub } from "../hub-types";
import type { ApiClient } from "../api-client";
import type { HttpTransport } from "../http/transport";
import type {
  VaultCredential,
  EmailProviderState,
  AutoresponderConnectionView,
} from "../client-types";

export type IntegrationsApiMethods = Pick<
  ApiClient,
  | "workspaceDefaults"
  | "getIntegrationsHub"
  | "connectHubGoogle"
  | "saveN8nInstance"
  | "removeN8nInstance"
  | "listConnectedApps"
  | "formResponsesExportUrl"
  | "acceptUpsellSuggestion"
  | "listGoogleSheetTabs"
  | "listGoogleSpreadsheets"
  | "createGoogleSpreadsheet"
  | "previewGoogleSheet"
  | "listCannedReplies"
  | "invoicePdfUrl"
  | "listApiKeys"
  | "createApiKey"
  | "commerceAuthorizeUrl"
  | "testCustomApi"
  | "connectCustomApi"
  | "rotateApiKey"
  | "revokeApiKey"
  | "listCredentials"
  | "deleteCredential"
  | "getEmailProvider"
  | "setEmailProvider"
  | "testEmailProvider"
  | "getGoogleContactsSync"
  | "setGoogleContactsSync"
  | "connectGoogleContacts"
  | "listAutoresponders"
  | "connectAutoresponder"
  | "disconnectAutoresponder"
  | "listWebhookEvents"
  | "listIncomingWebhooks"
  | "createIncomingWebhook"
  | "updateIncomingWebhook"
  | "deleteIncomingWebhook"
  | "listOutgoingWebhooks"
  | "createOutgoingWebhook"
  | "updateOutgoingWebhook"
  | "deleteOutgoingWebhook"
  | "rotateOutgoingWebhookSecret"
  | "testOutgoingWebhook"
  | "resumeOutgoingWebhook"
  | "listWebhookDeliveries"
  | "replayWebhookDelivery"
  | "broadcastOverview"
  | "broadcastStats"
  | "broadcastPreflight"
  | "broadcastCostPreview"
  | "getSystemReplies"
  | "listUnmatchedQuestions"
  | "proposeUnmatchedQuestion"
  | "dismissUnmatchedQuestion"
  | "listPipelines"
  | "createPipeline"
  | "updatePipelineStages"
  | "listDeals"
  | "moveDealStage"
  | "createDeal"
  | "updateDeal"
  | "deleteDeal"
>;

export function createIntegrationsApi({
  request,
  ws,
  base,
}: HttpTransport): IntegrationsApiMethods {
  const hub = (id: string) => `${ws(id)}/integrations/hub`;
  return {
    getIntegrationsHub: (id) => request<IntegrationsHub>("GET", hub(id)),
    connectHubGoogle: (id, feature) =>
      request<{ url: string }>("POST", `${hub(id)}/google/${feature}/connect`, {}),
    saveN8nInstance: async (id, input) => {
      await request("PUT", `${hub(id)}/n8n-instance`, input);
    },
    removeN8nInstance: async (id) => {
      await request("DELETE", `${hub(id)}/n8n-instance`);
    },
    workspaceDefaults: () => request<WorkspaceDefaults>("GET", "/v1/onboarding/workspace-defaults"),
    listConnectedApps: (id) => request<{ apps: ConnectedApp[] }>("GET", `${ws(id)}/oauth/grants`),
    formResponsesExportUrl: (id, formId, range) =>
      ws(id) +
      "/forms/" +
      encodeURIComponent(formId) +
      "/responses/export" +
      formResponsesQuery(range ?? {}),
    async acceptUpsellSuggestion(id, suggestionId) {
      await request<unknown>(
        "POST",
        `${ws(id)}/sell/upsell-suggestions/${encodeURIComponent(suggestionId)}/accept`,
        {},
      );
    },
    async listGoogleSheetTabs(id, spreadsheetId) {
      return (
        await request<{ tabs: { title: string; sheetId: number }[] }>(
          "GET",
          `${ws(id)}/integrations/google-sheets/spreadsheets/${encodeURIComponent(spreadsheetId)}/tabs`,
        )
      ).tabs;
    },
    async listGoogleSpreadsheets(id, opts) {
      const qs = new URLSearchParams();
      if (opts?.q) qs.set("q", opts.q);
      if (opts?.pageToken) qs.set("pageToken", opts.pageToken);
      const s = qs.toString();
      return request<{
        items: { id: string; name: string; modifiedTime: string }[];
        nextPageToken: string | null;
      }>("GET", `${ws(id)}/integrations/google-sheets/spreadsheets${s ? `?${s}` : ""}`);
    },
    createGoogleSpreadsheet: (id, input) =>
      request<{ id: string; name: string; url: string }>(
        "POST",
        `${ws(id)}/integrations/google-sheets/spreadsheets`,
        input,
      ),
    async previewGoogleSheet(id, spreadsheetId, tab) {
      return request<{ headers: string[]; rows: string[][] }>(
        "GET",
        `${ws(id)}/integrations/google-sheets/spreadsheets/${encodeURIComponent(spreadsheetId)}/preview?tab=${encodeURIComponent(tab)}`,
      );
    },
    async listCannedReplies(id) {
      return (
        await request<{ items: CannedReply[] }>("GET", `${ws(id)}/conversations/canned-replies`)
      ).items;
    },
    invoicePdfUrl: (id, invoiceId) =>
      `${base}${ws(id)}/billing/invoices/${encodeURIComponent(invoiceId)}/pdf`,
    listApiKeys: (id) => request<ApiKeysResponse>("GET", `${ws(id)}/api-keys`),
    createApiKey: (id, input) => request<CreatedApiKey>("POST", `${ws(id)}/api-keys`, input),
    commerceAuthorizeUrl: (id, provider, shop) =>
      `${base}${ws(id)}/commerce/connect/${provider}/authorize${shop ? `?shop=${encodeURIComponent(shop)}` : ""}`,
    testCustomApi: (id, input) =>
      request<CommerceTestResult>("POST", `${ws(id)}/commerce/connect/custom-api/test`, input),
    connectCustomApi: (id, input) =>
      request<CustomApiConnectResult>("POST", `${ws(id)}/commerce/connect/custom-api`, input),
    rotateApiKey: (id, keyId) =>
      request<CreatedApiKey>("POST", `${ws(id)}/api-keys/${encodeURIComponent(keyId)}/rotate`, {}),
    async revokeApiKey(id, keyId) {
      await request("DELETE", `${ws(id)}/api-keys/${encodeURIComponent(keyId)}`);
    },
    listCredentials: async (id) =>
      (
        await request<{ credentials: VaultCredential[] }>(
          "GET",
          `${ws(id)}/integrations/credentials`,
        )
      ).credentials,
    deleteCredential: async (id, credentialId, force = false) => {
      await request<unknown>(
        "DELETE",
        `${ws(id)}/integrations/credentials/${credentialId}${force ? "?force=1" : ""}`,
      );
    },
    getEmailProvider: (id) =>
      request<EmailProviderState>("GET", `${ws(id)}/integrations/email-provider`),
    setEmailProvider: async (id, input) => {
      await request<unknown>("PUT", `${ws(id)}/integrations/email-provider`, input);
    },
    testEmailProvider: async (id, to) => {
      await request<unknown>("POST", `${ws(id)}/integrations/email-provider/test`, { to });
    },
    getGoogleContactsSync: (id) =>
      request<{ enabled: boolean; status: string }>(
        "GET",
        `${ws(id)}/integrations/google-contacts`,
      ),
    setGoogleContactsSync: async (id, enabled) => {
      await request<unknown>("PUT", `${ws(id)}/integrations/google-contacts`, { enabled });
    },
    connectGoogleContacts: (id) =>
      request<{ url: string }>("POST", `${ws(id)}/integrations/google-contacts/connect`),
    listAutoresponders: async (id) =>
      (
        await request<{ connections: AutoresponderConnectionView[] }>(
          "GET",
          `${ws(id)}/integrations/autoresponders`,
        )
      ).connections,
    connectAutoresponder: async (id, input) => {
      await request<unknown>("POST", `${ws(id)}/integrations/autoresponders`, input);
    },
    disconnectAutoresponder: async (id, connectionId) => {
      await request<unknown>("DELETE", `${ws(id)}/integrations/autoresponders/${connectionId}`);
    },
    listWebhookEvents: async (id) =>
      (await request<{ events: string[] }>("GET", `${ws(id)}/webhooks/events`)).events,
    listIncomingWebhooks: async (id) =>
      (await request<{ endpoints: WebhookInEndpoint[] }>("GET", `${ws(id)}/webhooks/in`)).endpoints,
    createIncomingWebhook: (id, input) => request("POST", `${ws(id)}/webhooks/in`, input),
    updateIncomingWebhook: async (id, hookId, patch) => {
      await request("PATCH", `${ws(id)}/webhooks/in/${hookId}`, patch);
    },
    deleteIncomingWebhook: async (id, hookId) => {
      await request("DELETE", `${ws(id)}/webhooks/in/${hookId}`);
    },
    listOutgoingWebhooks: async (id) =>
      (await request<{ endpoints: WebhookOutEndpoint[] }>("GET", `${ws(id)}/webhooks/out`))
        .endpoints,
    createOutgoingWebhook: (id, input) => request("POST", `${ws(id)}/webhooks/out`, input),
    updateOutgoingWebhook: async (id, hookId, patch) => {
      await request("PATCH", `${ws(id)}/webhooks/out/${hookId}`, patch);
    },
    deleteOutgoingWebhook: async (id, hookId) => {
      await request("DELETE", `${ws(id)}/webhooks/out/${hookId}`);
    },
    rotateOutgoingWebhookSecret: (id, hookId) =>
      request("POST", `${ws(id)}/webhooks/out/${hookId}/rotate`),
    testOutgoingWebhook: (id, hookId) => request("POST", `${ws(id)}/webhooks/out/${hookId}/test`),
    resumeOutgoingWebhook: async (id, hookId) => {
      await request("POST", `${ws(id)}/webhooks/out/${hookId}/resume`, { acknowledge: true });
    },
    listWebhookDeliveries: async (id, hookId) =>
      (
        await request<{ deliveries: WebhookDeliveryAttempt[] }>(
          "GET",
          `${ws(id)}/webhooks/out/${hookId}/deliveries`,
        )
      ).deliveries,
    replayWebhookDelivery: async (id, hookId, eventId) => {
      await request("POST", `${ws(id)}/webhooks/out/${hookId}/deliveries/${eventId}/replay`);
    },
    broadcastOverview: (id) => request<BroadcastOverview>("GET", `${ws(id)}/broadcasts/overview`),
    broadcastStats: (id, days) =>
      request<BroadcastStats>("GET", `${ws(id)}/broadcasts/stats?days=${days}`),
    broadcastPreflight: (id, bid) =>
      request<BroadcastPreflight>(
        "GET",
        `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/preflight`,
      ),
    broadcastCostPreview: async (id, bid) =>
      // The API answers `{ preflight, cost }`; the wizard only needs the cost.
      (
        await request<{ cost: BroadcastCostPreview }>(
          "GET",
          `${ws(id)}/broadcasts/${encodeURIComponent(bid)}/cost-preview`,
        )
      ).cost,
    getSystemReplies: (id) => request<SystemRepliesView>("GET", `${ws(id)}/system-replies`),
    listUnmatchedQuestions: async (id) =>
      (await request<{ items: UnmatchedQuestionView[] }>("GET", `${ws(id)}/automation/unmatched`))
        .items,
    proposeUnmatchedQuestion: async (id, questionId, action) =>
      (
        await request<{ proposal: { conversationId: string } }>(
          "POST",
          `${ws(id)}/automation/unmatched/${encodeURIComponent(questionId)}/propose`,
          { action },
        )
      ).proposal,
    dismissUnmatchedQuestion: async (id, questionId) => {
      await request<unknown>(
        "DELETE",
        `${ws(id)}/automation/unmatched/${encodeURIComponent(questionId)}`,
      );
    },
    listPipelines: async (id) =>
      (await request<{ items: PipelineView[] }>("GET", ws(id) + "/pipelines")).items,
    createPipeline: (id, input) => request<PipelineView>("POST", ws(id) + "/pipelines", input),
    updatePipelineStages: (id, pipelineId, stages) =>
      request<PipelineView>("PATCH", ws(id) + "/pipelines/" + encodeURIComponent(pipelineId), {
        stages,
      }),
    listDeals: async (id, filter) => {
      const qs = new URLSearchParams();
      if (filter?.pipelineId) qs.set("pipelineId", filter.pipelineId);
      if (filter?.contactId) qs.set("contactId", filter.contactId);
      const q = qs.toString();
      return (await request<{ items: DealView[] }>("GET", ws(id) + "/deals" + (q ? "?" + q : "")))
        .items;
    },
    moveDealStage: (id, dealId, stageKey, winLossReason) =>
      request<DealView>("PATCH", ws(id) + "/deals/" + encodeURIComponent(dealId) + "/stage", {
        stageKey,
        ...(winLossReason ? { winLossReason } : {}),
      }),
    createDeal: (id, input) => request<DealView>("POST", ws(id) + "/deals", input),
    updateDeal: (id, dealId, input) =>
      request<DealView>("PATCH", ws(id) + "/deals/" + encodeURIComponent(dealId), input),
    deleteDeal: async (id, dealId) => {
      await request<unknown>("DELETE", ws(id) + "/deals/" + encodeURIComponent(dealId));
    },
  };
}
